import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { sharedData } from "./model-state";

export const isPortrait = window.matchMedia("(orientation: portrait)").matches && window.innerWidth <= 500;

export const buildScene = () => {
  const scene = new THREE.Scene();
  if (isPortrait) {
    scene.rotation.z = Math.PI / 2;
  }
  return scene;
};

export const buildRenderer = ({ width, height }, canvas) => {
  const renderer = new THREE.WebGLRenderer({
    canvas: canvas,
    antialias: true,
    alpha: true,
    preserveDrawingBuffer: true,
  });
  const DPR = window.devicePixelRatio ? window.devicePixelRatio : 1;
  renderer.setPixelRatio(DPR);
  renderer.setSize(width, height);
  renderer.shadowMap.enabled = true;
  // renderer.gammaInput = true;
  // renderer.gammaOutput = true;
  renderer.toneMapping = THREE.LinearToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.outputEncoding = THREE.sRGBEncoding;
  return renderer;
};

export const buildControls = (camera, renderer) => {
  var orbitControls = new OrbitControls(camera, renderer.domElement);
  orbitControls.target.set(0, 0, 20);
  orbitControls.enableDamping = true;
  orbitControls.dampingFactor = 0.05;
  orbitControls.screenSpacePanning = true;
  orbitControls.minDistance = 10;
  orbitControls.maxDistance = 1000;
  orbitControls.enablePan = true;
  orbitControls.zoomSpeed = 0.5;
  if (isPortrait) {
    orbitControls.rotateSpeed = -0.5;
  } else {
    orbitControls.rotateSpeed = 0.5;
  }
  return orbitControls;
};

export const buildCamera = ({ width, height }) => {
  var frustumSize = 200;
  const aspectRatio = width / height;
  // const camera = new THREE.PerspectiveCamera();
  const camera = new THREE.OrthographicCamera(
    frustumSize * aspectRatio,
    frustumSize * aspectRatio,
    frustumSize,
    frustumSize
    // -1000,
    // 500
  );

  // camera.fov = 45;
  // camera.aspect = aspectRatio;
  // camera.far = 1000;
  // camera.near = 0.5;
  // camera.position.set(0, 0, 112);

  camera.position.set(0, 0, 112);
  // Orthographic cameras zoom via camera.zoom (not by moving camera position).
  // Keep mobile at default zoom to avoid an over-zoomed initial view.
  if (!isPortrait) {
    camera.zoom = 1.21;
  }
  camera.updateProjectionMatrix();

  // var target = new THREE.Vector3(0, 0, 0);
  // camera.lookAt(target);

  return camera;
};

export const buildLight = (scene, renderer) => {
  // const light = new THREE.DirectionalLight(0xffffff);

  // light.position.set(0, 1, 0);
  // light.castShadow = false;
  // scene.add(light);

  const mainLight = new THREE.DirectionalLight(0xffffff, 1.8);
  // mainLight.position.set(5, 10, 5);
  mainLight.castShadow = true;
  mainLight.shadow.camera.near = 0.1;
  mainLight.shadow.camera.far = 50;
  if (isPortrait) {
    mainLight.shadow.camera.left = 20;
    mainLight.shadow.camera.right = -20;
    mainLight.shadow.camera.top = -20;
    mainLight.shadow.camera.bottom = 20;
  } else {
    mainLight.shadow.camera.left = -20;
    mainLight.shadow.camera.right = 20;
    mainLight.shadow.camera.top = 20;
    mainLight.shadow.camera.bottom = -20;
  }

  mainLight.shadow.mapSize.width = 2048;
  mainLight.shadow.mapSize.height = 2048;
  // mainLight.shadow.bias = -0.0005;
  scene.add(mainLight);

  const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);

  scene.add(fillLight);

  const rimLight = new THREE.DirectionalLight(0xffffff, 0.65);

  scene.add(rimLight);

  const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
  scene.add(ambientLight);

  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputEncoding = THREE.sRGBEncoding;
  renderer.physicallyCorrectLights = true;

  if (!isPortrait) {
    fillLight.position.set(-5, 5, 5);
    rimLight.position.set(0, 5, -10);
  } else {
    fillLight.position.set(5, -5, 5);
    rimLight.position.set(-5, 0, -10);
  }

  return mainLight;
};

export const buildGrid = ({ width, height }) => {
  const aspect = width / height;
  const overlayCamera = new THREE.OrthographicCamera(
    -1 * aspect,
    1 * aspect,
    1,
    -1,
    0,
    1
  );
  const overlayScene = new THREE.Scene();
  overlayScene.visible = sharedData.isGridVisible;

  // Regular grid
  const gridGeometry = new LineSegmentsGeometry();
  const positions = [];

  const step = 0.05;
  const maxExtent = Math.max(aspect, 1) * 2;
  const range = Math.ceil(maxExtent / step);

  // Vertical lines (excluding center line)
  for (let i = -range; i <= range; i++) {
    if (i === 0) continue; // Skip center line
    const x = i * step;
    positions.push(x, -range * step, 0, x, range * step, 0);
  }

  // Horizontal lines (excluding center line)
  for (let i = -range; i <= range; i++) {
    if (i === 0) continue; // Skip center line
    const y = i * step;
    positions.push(-range * step, y, 0, range * step, y, 0);
  }

  gridGeometry.setPositions(positions);

  const gridMaterial = new LineMaterial({
    color: 0x888888,
    linewidth: 2,
    transparent: true,
    opacity: 0.3,
  });
  gridMaterial.resolution.set(window.innerWidth, window.innerHeight);

  const grid = new LineSegments2(gridGeometry, gridMaterial);
  overlayScene.add(grid);

  // Center lines (thicker)
  const centerGeometry = new LineSegmentsGeometry();
  const centerPositions = [
    // Vertical center line
    0,
    -range * step,
    0,
    0,
    range * step,
    0,
    // Horizontal center line
    -range * step,
    0,
    0,
    range * step,
    0,
    0,
  ];
  centerGeometry.setPositions(centerPositions);

  const centerMaterial = new LineMaterial({
    color: 0x888888,
    linewidth: 3.5, // Thicker line
    transparent: true,
    opacity: 0.5, // Slightly more opaque
  });
  centerMaterial.resolution.set(window.innerWidth, window.innerHeight);

  const centerLines = new LineSegments2(centerGeometry, centerMaterial);
  overlayScene.add(centerLines);

  return { overlayCamera, overlayScene, gridMaterial, centerMaterial };
};
